import React from "react";
import * as PropTypes from "prop-types";

const COLOR_COUNT = 8;

const DATE_COLUMNS = ["DATA_INICIO", "DATA_FIM", "DATA_EVENTO"];

// Valores fictícios no formato bruto em que o backend lê cada coluna (IntegracaoController@generateLine)
const SAMPLE_VALUES = {
  CODIGO_RUBRICA    : "100",
  FUNCIONARIO_FOLHA : "1234",
  EMPRESA_FOLHA     : "1",
  DEPARTAMENTO_FOLHA: "10",
  PIS               : "12345678901",
  CPF               : "12345678909",
  NOME_FUNCIONARIO  : "Maria Silva",
  DEPARTAMENTO      : "Financeiro",
  FUNCAO            : "Analista",
  DOCUMENTO_EMP     : "12345678000199",
  NOME_EMPRESA      : "Empresa Exemplo",
  VALOR             : "08:00",
};

const onlyDigits = (value) => value.replace(/\D/g, "");

const formatDecimal = (value) => {
  if (value.includes(":")) return value.replace(":", ",");

  const number = parseFloat(value.replace(/\./g, "").replace(",", "."));

  return Number.isNaN(number) ? value : number.toFixed(2).replace(".", ",");
};

// Espelha IntegracaoController@formatByType; datas e horas usam o exemplo do próprio formato
const formatValue = (row, examples) => {
  if (row.column === "TEXTO_FIXO") return String(row.eventCode ?? "");

  if (DATE_COLUMNS.includes(row.column)) return examples[row.type] ?? "";

  const value = SAMPLE_VALUES[row.column] ?? "";

  switch (row.type) {
    case "CPF_FORMATADO": {
      const digits = onlyDigits(value);
      return digits.length === 11 ? digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : digits;
    }
    case "DECIMAL":
      return formatDecimal(value);
    case "NUMERO":
      return onlyDigits(value);
    default:
      return value;
  }
};

// Mesmo preenchimento do backend: zeros à esquerda até o tamanho e corte no limite
const fitToLimit = (value, limit) => {
  const size = Math.max(1, parseInt(limit, 10) || 1);

  return value.length < size ? value.padStart(size, "0") : value.slice(0, size);
};

const buildPreviewSegments =(rows, typeSections, columnOptions) => {
  const examples = {};
  (typeSections ?? []).forEach((section) => (section.options ?? []).forEach((option) => {
    examples[option.value] = option.example;
  }));

  const labels = {};
  (columnOptions ?? []).forEach((section) => (section.options ?? []).forEach((option) => {
    labels[option.value] = option.label;
  }));

  return (rows ?? []).map((row, index) => {
    const separator = row.separator ?? "";

    if (!row.column) {
      return { key: index, value: "", separator, label: null, color: index % COLOR_COUNT };
    }

    return {
      key      : index,
      value    : fitToLimit(formatValue(row, examples), row.limit),
      separator,
      label    : labels[row.column] ?? row.column,
      color    : index % COLOR_COUNT,
    };
  });
};

const LayoutPreview = ({rows, typeSections, columnOptions}) => {
  const segments = buildPreviewSegments(rows, typeSections, columnOptions);
  const hasValue = segments.some((segment) => segment.value || segment.separator);

  return (
    <div className="integration-preview">
      <div className="integration-preview-line">
        {hasValue ? segments.map((segment) => (
          <React.Fragment key={segment.key}>
            <span className={`integration-preview-color-${segment.color}`}>{segment.value}</span>
            {segment.separator && <span className="integration-preview-separator">{segment.separator}</span>}
          </React.Fragment>
        )) : <span className="integration-preview-empty">Adicione colunas para visualizar o registro.</span>}
      </div>
      {segments.some((segment) => segment.label) && (
        <div className="integration-preview-legend">
          <span className="integration-preview-legend-title">Legenda:</span>
          <div className="integration-preview-legend-items">
            {segments.filter((segment) => segment.label).map((segment) => (
              <span key={segment.key} className={`integration-preview-legend-item integration-preview-color-${segment.color}`}>
                <i className="fas fa-square" />
                {segment.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

LayoutPreview.propTypes = {
  rows         : PropTypes.array,
  typeSections : PropTypes.array,
  columnOptions: PropTypes.array,
};

LayoutPreview.defaultProps = {
  rows         : [],
  typeSections : [],
  columnOptions: [],
};

export default LayoutPreview;
